import { h, defineComponent, ref, computed, getCurrentInstance } from 'vue';
// @ts-ignore
import { BPagination, BPaginationButton } from 'buefy/dist/esm/pagination';
import { Icon } from '@/ui/components/icon/Icon';
import { TaskState } from '../concerns';
import { router, useCurrentRoute } from '@/ui/routes';
import { inputField } from '@/ui/components/form';
import { t } from '@/ui/i18n/i18n';
import { Button } from '@/ui/components/Button';
import Spinner from '@/ui/components/Spinner.vue';

export interface PaginationProps {
  current: number;
  perPage: number;
  total: number;
  totalLoading?: boolean;
  showCustomPagination?: boolean;
}

const firstPageNumber = 1;

export const Pagination = defineComponent<PaginationProps>({
  name: 'Pagination',
  props: ['current', 'perPage', 'total', 'totalLoading', 'showCustomPagination'],
  setup(props) {
    const currentRoute = useCurrentRoute();
    const instance = getCurrentInstance();
    const showCustomPagination = props.showCustomPagination ?? true;

    const pageCount = computed<number>(() => Math.ceil(props.total / props.perPage));

    const pageNumber = ref<number>(props.current);

    const isButtonDisabled = computed<boolean>(
      () => pageNumber.value === props.current || pageNumber.value > pageCount.value || pageNumber.value < firstPageNumber,
    );

    const hasPrev = computed<boolean>(() => pageNumber.value > firstPageNumber);
    const hasNext = computed<boolean>(() => pageNumber.value < pageCount.value);

    const onChangePage = () => {
      if (!isButtonDisabled.value) {
        router.push({
          ...currentRoute,
          query: {
            ...(instance?.proxy?.$route.query ?? {}),
            page: pageNumber.value.toString(),
          },
        });
      }
    };

    return function render() {
      if (props.total <= props.perPage) {
        return null;
      }

      const lastPage = Math.ceil(props.total / props.perPage);

      return h('div', { class: 'pagination-wrapper' }, [
        h(BPagination, {
          props: {
            current: props.current,
            order: 'is-centered',
            perPage: props.perPage,
            total: props.total,
          },
          scopedSlots: {
            default: scopedProps => [
              props.totalLoading && scopedProps.page.number === lastPage ? h(Spinner)
                : h(BPaginationButton, {
                  props: { tag: 'router-link', page: scopedProps.page },
                  attrs: {
                    to: {
                      path: instance?.proxy?.$route.path,
                      query: {
                        ...(instance?.proxy?.$route.query ?? {}),
                        page: scopedProps.page.number.toString(),
                      },
                    },
                  },
                }, scopedProps.page.number),
            ],
            previous: scopedProps => [
              h(BPaginationButton, {
                props: {
                  tag: hasPrev.value ? 'router-link' : 'button',
                  page: scopedProps.page,
                  iconPrev: 'angle-left',
                  iconNext: 'angle-right',
                },
                attrs: {
                  to: {
                    path: instance?.proxy?.$route.path,
                    query: {
                      ...(instance?.proxy?.$route.query ?? {}),
                      page: scopedProps.page.number.toString(),
                    },
                  },
                },
              }, [h(Icon, { props: { icon: 'chevron-left' } })]),
            ],
            next: scopedProps => [
              h(BPaginationButton, {
                props: { tag: hasNext.value ? 'router-link' : 'button', page: scopedProps.page },
                attrs: {
                  to: {
                    path: instance?.proxy?.$route.path,
                    query: {
                      ...(instance?.proxy?.$route.query ?? {}),
                      page: scopedProps.page.number.toString(),
                    },
                  },
                },
              }, [h(Icon, { props: { icon: 'chevron-right' } })]),
            ],
          },
        }),
        showCustomPagination && h('div', { class: 'custom-pagination' }, [
          h('span', { class: 'custom-pagination__label' }, t('common.page')),
          h('div', {
            class: 'custom-pagination__input',
          }, [
            inputField({
              name: 'pageNumber',
              type: 'number',
              label: '',
              placeholder: '',
              value: pageNumber.value,
              required: false,
              disabled: false,
            }, value => {
              pageNumber.value = Number(value);
            }, {
              'input:enter': onChangePage,
            }),
          ]),
          h(Button, {
            class: 'custom-pagination__button',
            props: {
              type: 'is-secondary',
              disabled: isButtonDisabled.value,
            },
            on: { click: onChangePage },
          }, t('common.go')),
        ]),
      ]);
    };
  },
});

export function pagination(props: PaginationProps) {
  return h(Pagination, { props });
}

export function taskPagination(state: TaskState, props: PaginationProps) {
  return 'success' in state ? h(Pagination, { props }) : null;
}
